import type { Phase } from "@shared/types";
import type { MessageKey } from "../i18n/en";

type TFn = (key: MessageKey, params?: Record<string, string | number>) => string;

// Phase-aware copy for the race (candidacy hub) page. The race-candidacies
// endpoint returns only `cycleStartAt` + `seats` (no phase), so when the user
// arrives without an in-app phase hint we infer a coarse phase from the clock.

/** Coarse phase from the race's cycle start: future → "soon", else "live".
 * We can't tell "done" from `cycleStartAt` alone, so that only comes from an
 * explicit hint passed by the navigating page. */
export function deriveRacePhase(cycleStartAt: number, now: number = Date.now()): Extract<Phase, "soon" | "live"> {
  return cycleStartAt > now ? "soon" : "live";
}

/** Reconcile a phase hint from the navigating page with the race's cycle
 * start. A "done" hint refers to a *previous* election, but this page lists
 * the current cycle's declarations — so when the cycle hasn't started yet the
 * hint is stale and we derive from the clock instead ("concluded · held
 * <future date>" would be nonsense). */
export function resolveRacePhase(hint: Phase | null, cycleStartAt: number, now: number = Date.now()): Phase {
  if (hint === "done" && cycleStartAt > now) return deriveRacePhase(cycleStartAt, now);
  return hint ?? deriveRacePhase(cycleStartAt, now);
}

/** The sentence under the race title, tailored to the phase. */
export function raceBlurb(
  { phase, cycleStartAt, seats }: { phase: Phase; cycleStartAt: number; seats: number | null },
  t: TFn,
  locale = "en",
): string {
  const date = new Date(cycleStartAt).toLocaleDateString(locale);
  const seatsSuffix = seats && seats > 1 ? ` · ${t("race.electsSeats", { n: seats })}` : "";
  if (phase === "done") {
    return `${t("race.concluded", { date })}${seatsSuffix}. ${t("race.selfReportedDone")}`;
  }
  const lead = phase === "live" ? t("race.underway", { date }) : t("race.upcoming", { date });
  return `${lead}${seatsSuffix}. ${t("race.selfReported")}`;
}
